import { toast as sonnerToast } from 'sonner';
import type { TFunction } from 'i18next';
import { ApiError } from '../api';

// Enhanced notification types
export interface NotificationOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  description?: string;
  important?: boolean;
  dismissible?: boolean;
}

export interface ProgressNotificationOptions extends NotificationOptions {
  progress?: number;
  total?: number;
  current?: number;
  estimatedTime?: string;
}

// Enhanced notification system
export class NotificationSystem {
  private t: TFunction;

  constructor(t: TFunction) {
    this.t = t;
  }

  // Success notifications
  success(message: string, options?: NotificationOptions) {
    return sonnerToast.success(message, {
      duration: options?.duration || 4000,
      dismissible: options?.dismissible !== false,
      action: options?.action,
      description: options?.description,
      className: options?.important ? 'border-green-500' : undefined,
    });
  }

  // Error notifications with enhanced error handling
  error(error: unknown, context?: string, options?: NotificationOptions) {
    let message: string;
    let description: string | undefined;
    let action: NotificationOptions['action'] | undefined;

    if (error instanceof ApiError) {
      message = this.getApiErrorMessage(error);
      description = this.getApiErrorDescription(error);
      action = this.getApiErrorAction(error);
    } else if (error instanceof Error) {
      message = error.message;
      description = context ? this.t('errors.generic', { message: context }) : undefined;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      message = this.t('errors.unknown');
    }

    return sonnerToast.error(message, {
      duration: options?.duration || 6000,
      dismissible: options?.dismissible !== false,
      description: options?.description || description,
      action: options?.action || action,
      className: 'border-red-500',
    });
  }

  // Warning notifications
  warning(message: string, options?: NotificationOptions) {
    return sonnerToast.warning(message, {
      duration: options?.duration || 5000,
      dismissible: options?.dismissible !== false,
      action: options?.action,
      description: options?.description,
      className: 'border-yellow-500',
    });
  }

  // Info notifications
  info(message: string, options?: NotificationOptions) {
    return sonnerToast.info(message, {
      duration: options?.duration || 4000,
      dismissible: options?.dismissible !== false,
      action: options?.action,
      description: options?.description,
    });
  }

  // Loading notifications with progress
  loading(message: string, options?: ProgressNotificationOptions) {
    const progressText = this.getProgressText(options);
    const fullMessage = progressText ? `${message} - ${progressText}` : message;

    return sonnerToast.loading(fullMessage, {
      duration: Infinity, // Loading notifications don't auto-dismiss
      dismissible: options?.dismissible !== false,
      description: options?.description,
    });
  }

  // Promise-based notifications with progress
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    },
    options?: ProgressNotificationOptions
  ) {
    return sonnerToast.promise(promise, {
      loading: options ? `${messages.loading} - ${this.getProgressText(options)}` : messages.loading,
      success: (data) => {
        const successMsg = typeof messages.success === 'function' 
          ? messages.success(data) 
          : messages.success;
        return successMsg;
      },
      error: (error) => {
        const errorMsg = typeof messages.error === 'function' 
          ? messages.error(error) 
          : messages.error;
        return errorMsg;
      },
    });
  }

  // Update existing notification (useful for progress updates)
  update(id: string | number, message: string, options?: ProgressNotificationOptions) {
    const progressText = this.getProgressText(options);
    const fullMessage = progressText ? `${message} - ${progressText}` : message;

    // Sonner doesn't have direct update, so we dismiss and create new
    sonnerToast.dismiss(id);
    return this.loading(fullMessage, options);
  }

  // Dismiss notification
  dismiss(id?: string | number) {
    return sonnerToast.dismiss(id);
  }

  // Dismiss all notifications
  dismissAll() {
    return sonnerToast.dismiss();
  }

  // Custom notification with JSX content
  custom(jsx: (id: string | number) => React.ReactElement, options?: NotificationOptions) {
    return sonnerToast.custom(jsx, {
      duration: options?.duration || 4000,
      dismissible: options?.dismissible !== false,
    });
  }

  // Specialized notifications for common operations
  operationSuccess(operation: string, entity?: string) {
    const key = `notifications.success.${operation}` as const;
    const message = this.t(key, { entity });
    return this.success(message);
  }

  operationError(operation: string, error: unknown, entity?: string) {
    const context = entity ? `${operation} ${entity}` : operation;
    return this.error(error, context);
  }

  // Connection status notifications
  connectionLost() {
    return this.warning(this.t('errors.connectionLost'), {
      duration: Infinity,
      dismissible: false,
    });
  }

  connectionRestored() {
    return this.success(this.t('errors.connectionRestored'), {
      duration: 3000,
    });
  }

  // Private helper methods
  private getApiErrorMessage(error: ApiError): string {
    if (error.isNetworkError) {
      return this.t('errors.network');
    }
    if (error.isServerError) {
      return this.t('errors.server');
    }
    if (error.isUnauthorized) {
      return this.t('errors.unauthorized');
    }
    if (error.isForbidden) {
      return this.t('errors.forbidden');
    }
    if (error.isNotFound) {
      return this.t('errors.notFound');
    }
    if (error.isValidationError) {
      return this.t('errors.validation');
    }
    if (error.status === 408) {
      return this.t('errors.timeout');
    }
    if (error.status === 429) {
      return this.t('errors.rateLimited');
    }
    if (error.status === 503) {
      return this.t('errors.maintenance');
    }

    return error.message || this.t('errors.unknown');
  }

  private getApiErrorDescription(error: ApiError): string | undefined {
    if (error.isNetworkError) {
      return this.t('notifications.error.checkConnection');
    }
    if (error.isServerError) {
      return this.t('notifications.error.contactSupport');
    }
    if (error.isUnauthorized || error.isForbidden) {
      return this.t('notifications.error.contactSupport');
    }
    if (error.status === 408) {
      return this.t('notifications.error.tryAgain');
    }
    if (error.status === 429) {
      return this.t('notifications.error.tryAgain');
    }

    return undefined;
  }

  private getApiErrorAction(error: ApiError): NotificationOptions['action'] | undefined {
    if (error.isNetworkError || error.status === 408) {
      return {
        label: this.t('notifications.error.tryAgain'),
        onClick: () => window.location.reload(),
      };
    }
    if (error.isServerError) {
      return {
        label: this.t('notifications.error.refreshPage'),
        onClick: () => window.location.reload(),
      };
    }

    return undefined;
  }

  private getProgressText(options?: ProgressNotificationOptions): string | undefined {
    if (!options) return undefined;

    const parts: string[] = [];

    if (options.progress !== undefined) {
      parts.push(this.t('notifications.progress.percentage', { percent: Math.round(options.progress) }));
    } else if (options.current !== undefined && options.total !== undefined) {
      const percent = Math.round((options.current / options.total) * 100);
      parts.push(this.t('notifications.progress.percentage', { percent }));
      parts.push(this.t('notifications.progress.itemsProcessed', { 
        processed: options.current, 
        total: options.total 
      }));
    }

    if (options.estimatedTime) {
      parts.push(this.t('notifications.progress.estimatedTime', { time: options.estimatedTime }));
    }

    return parts.length > 0 ? parts.join(' • ') : undefined;
  }
}

// Hook for using the notification system
export const useNotifications = (t: TFunction) => {
  return new NotificationSystem(t);
};

// Legacy compatibility - enhanced toast object
export const createEnhancedToast = (t: TFunction) => {
  const notifications = new NotificationSystem(t);

  return {
    success: (message: string, options?: NotificationOptions) => 
      notifications.success(message, options),
    
    error: (error: unknown, context?: string, options?: NotificationOptions) => 
      notifications.error(error, context, options),
    
    warning: (message: string, options?: NotificationOptions) => 
      notifications.warning(message, options),
    
    info: (message: string, options?: NotificationOptions) => 
      notifications.info(message, options),
    
    loading: (message: string, options?: ProgressNotificationOptions) => 
      notifications.loading(message, options),
    
    promise: <T>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: unknown) => string);
      },
      options?: ProgressNotificationOptions
    ) => notifications.promise(promise, messages, options),
    
    dismiss: (id?: string | number) => notifications.dismiss(id),
    
    custom: (jsx: (id: string | number) => React.ReactElement, options?: NotificationOptions) => 
      notifications.custom(jsx, options),

    // Convenience methods
    operationSuccess: (operation: string, entity?: string) => 
      notifications.operationSuccess(operation, entity),
    
    operationError: (operation: string, error: unknown, entity?: string) => 
      notifications.operationError(operation, error, entity),
  };
};